// @flow
import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { stylesheet } from 'typestyle';
import * as csx from 'csstips';

import ShapePicker from '@components/ShapePicker';
import PriceRange from '@components/sliders/PriceRange';
import ColorSlider from '@components/sliders/ColorSlider';
import ClaritySlider from '@components/sliders/ClaritySlider';
import CaratWeightRange from '@components/sliders/CaratWeightRange';
import CutSlider from '@components/sliders/CutSlider';
import PolishSlider from '@components/sliders/PolishSlider';
import SymmetrySlider from '@components/sliders/SymmetrySlider';
import FluorescenceSlider from '@components/sliders/FluorescenceSlider';
import DepthSlider from '@components/sliders/DepthSlider';
import LengthWidthSlider from '@components/sliders/LengthWidthSlider';
import TableSlider from '@components/sliders/TableSlider';
import ErrorBoundary from '@components/ErrorBoundary';

import { blueButton, lightGrayButton } from '@styles/buttons';

type TProps = {
  dyor: boolean,
  onFilterChange?: () => void,
  disableUrlSync?: boolean
};

const css = stylesheet({
  filterContainer: {
    overflow: 'visible', // This was 'hidden' until we added the popups, now we rely on the parent's overflow setting
    maxHeight: '1000px', // only needed to perform a smooth CSS transition
    padding: '0 5px 0 7px',
    transition: 'all 600ms cubic-bezier(0.19, 1, 0.22, 1)',
    position: 'relative',
    zIndex: 1,
    display: 'block',
    borderTop: '1px solid #e1e1e1'
  },
  sliderContainer: {
    width: '16%',

    '@media (max-width: 669px)': {
      width: '50%'
    },

    '@media (min-width: 670px) and (max-width: 1019px)': {
      width: '25%'
    }
  },
  filters: {
    ...csx.horizontal,
    ...csx.wrap,
    ...csx.aroundJustified,
    marginTop: '21px',
    width: '200%', // this allows for a smooth transition using css transforms
    transform: 'translateX(0)',
    transition: 'all 600ms cubic-bezier(0.19, 1, 0.22, 1)',

    '@media (max-width: 669px)': {
      '& > div': {
        '&:nth-child(1)': { order: 1 },
        '&:nth-child(2)': { order: 3 },
        '&:nth-child(3)': { order: 5 },
        '&:nth-child(4)': { order: 2 },
        '&:nth-child(5)': { order: 4 },
        '&:nth-child(6)': { order: 6 },
        '&:nth-child(7)': { order: 7 },
        '&:nth-child(8)': { order: 9 },
        '&:nth-child(9)': { order: 11 },
        '&:nth-child(10)': { order: 8 },
        '&:nth-child(11)': { order: 10 },
        '&:nth-child(12)': { order: 12 }
      }
    },

    '@media (min-width: 670px) and (max-width: 1019px)': {
      '& > div': {
        '&:nth-child(1)': { order: 1 },
        '&:nth-child(2)': { order: 2 },
        '&:nth-child(3)': { order: 5 },
        '&:nth-child(4)': { order: 3 },
        '&:nth-child(5)': { order: 4 },
        '&:nth-child(6)': { order: 7 },
        '&:nth-child(7)': { order: 6 },
        '&:nth-child(8)': { order: 9 },
        '&:nth-child(9)': { order: 10 },
        '&:nth-child(10)': { order: 8 },
        '&:nth-child(11)': { order: 11 },
        '&:nth-child(12)': { order: 12 }
      }
    },

    'media (min-width: 1020px)': {
      '& > div': {
        '&:nth-child(1)': { order: 1 },
        '&:nth-child(2)': { order: 2 },
        '&:nth-child(3)': { order: 3 },
        '&:nth-child(4)': { order: 4 },
        '&:nth-child(5)': { order: 5 },
        '&:nth-child(6)': { order: 6 },
        '&:nth-child(7)': { order: 7 },
        '&:nth-child(8)': { order: 8 },
        '&:nth-child(9)': { order: 9 },
        '&:nth-child(10)': { order: 10 },
        '&:nth-child(11)': { order: 11 },
        '&:nth-child(12)': { order: 12 }
      }
    }
  },
  advancedSearchLinkContainer: {
    ...csx.horizontal,
    ...csx.center,
    ...csx.betweenJustified,
    height: '42px',
    padding: '0 35px',
    color: '#31528d',
    fontSize: '14px',
    borderBottom: '1px solid rgb(228, 228, 228)'
  },
  advancedSearchLinks: {
    marginBottom: '14px',
    cursor: 'pointer',
    $nest: {
      '&:hover': {
        textDecoration: 'underline'
      }
    }
  },
  diamondType: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '50px',
    marginTop: '24px',
    $nest: {
      '& button': {
        opacity: 1,
        width: '250px',
        ...blueButton,
      },

      '& button:not([disabled])': {
        ...lightGrayButton,
      },
    },

    '@media (max-width: 669px)': {
      flexDirection: 'column',
      gap: '15px',
    },
  },
});

function Filters({ dyor, onFilterChange, disableUrlSync = false }: TProps) {
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const searchExtents = useSelector(state => state.diamondSearchExtents);
  const filters = useSelector(state => state.diamondSearchFilters);
  const showFilters = useSelector(state => state.diamondSearch.showFilters);
  const dispatch = useDispatch();

  const handleSliderUpdate = useCallback(
    (type, item) => {
      dispatch({
        type: 'DIAMOND_SLIDER_UPDATE',
        action: { type, item },
        looseDiamonds: !dyor,
        disableUrlSync,
        onSuccess: onFilterChange
      });
    },
    [dispatch, dyor, disableUrlSync, onFilterChange]
  );

  const handleClearFiltersClick = useCallback(() => {
    setShowAdvancedSearch(false);

    dispatch({
      type: 'CLEAR_DIAMOND_SEARCH_FILTERS_CLICK',
      looseDiamonds: !dyor,
      disableUrlSync
    });
  }, [dispatch, dyor, disableUrlSync]);

  const handleShapeChange = useCallback(
    shape => {
      dispatch({
        type: 'TOGGLE_DIAMOND_SHAPE',
        shape: shape,
        looseDiamonds: !dyor,
        disableUrlSync,
        onSuccess: onFilterChange
      });
    },
    [dispatch, dyor, disableUrlSync, onFilterChange]
  );

  const handleChangeType = useCallback(
    type => {
      dispatch({
        type: 'CHANGE_DIAMOND_TYPE',
        item: type,
        looseDiamonds: !dyor,
        disableUrlSync,
        onSuccess: onFilterChange,
      });
    },
    [dispatch, dyor, disableUrlSync, onFilterChange]
  );

  return (
    <div
      className={css.filterContainer}
      style={!showFilters ? { maxHeight: '0', overflow: 'hidden' } : {}}
    >
      <div className={css.diamondType}>
        <button
          disabled={filters.diamond_type === 'natural'}
          onClick={() => handleChangeType('natural')}
        >
          Natural Diamonds
        </button>
        <button
          disabled={filters.diamond_type === 'lab-grown'}
          onClick={() => handleChangeType('lab-grown')}
        >
          Lab Created Diamonds
        </button>
      </div>
      <div
        className={css.filters}
        style={showAdvancedSearch ? { transform: 'translateX(-50%)' } : {}}
      >
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              selectableShapes: searchExtents.shape,
              selectedShapes: filters.shape
            }}
          >
            <ShapePicker
              selectableShapes={dyor ? searchExtents.shape : undefined}
              selectedShapes={filters.shape}
              onShapeChange={handleShapeChange}
              hiddenShapes={dyor ? ['R'] : []}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              value: filters.price,
              extent: searchExtents.price
            }}
          >
            <PriceRange
              value={filters.price}
              extent={searchExtents.price}
              onSliderUpdate={handleSliderUpdate}
              disableTabIndex={showAdvancedSearch}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              extents: searchExtents.carat,
              filters: filters.carat
            }}
          >
            <CaratWeightRange
              value={filters.carat}
              extent={searchExtents.carat}
              onSliderUpdate={handleSliderUpdate}
              disableTabIndex={showAdvancedSearch}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.polish} fallback={false}>
            <PolishSlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.polish}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.symmetry} fallback={false}>
            <SymmetrySlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.symmetry}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.fluorescence} fallback={false}>
            <FluorescenceSlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.fluorescence}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.cut} fallback={false}>
            <CutSlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.cut}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.color} fallback={false}>
            <ColorSlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.color}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary resetOnChanges={filters.clarity} fallback={false}>
            <ClaritySlider
              onSliderUpdate={handleSliderUpdate}
              value={filters.clarity}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              value: filters.depth,
              extent: searchExtents.depth
            }}
          >
            <DepthSlider
              value={filters.depth}
              extent={searchExtents.depth}
              onSliderUpdate={handleSliderUpdate}
              disableTabIndex={!showAdvancedSearch}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              value: filters.table,
              extent: searchExtents.table
            }}
          >
            <TableSlider
              value={filters.table}
              extent={searchExtents.table}
              onSliderUpdate={handleSliderUpdate}
              disableTabIndex={!showAdvancedSearch}
            />
          </ErrorBoundary>
        </div>
        <div className={css.sliderContainer}>
          <ErrorBoundary
            fallback={false}
            resetOnChanges={{
              value: filters.lw_ratio,
              extent: searchExtents.lw_ratio
            }}
          >
            <LengthWidthSlider
              value={filters.lw_ratio}
              extent={searchExtents.lw_ratio}
              onSliderUpdate={handleSliderUpdate}
              disableTabIndex={!showAdvancedSearch}
            />
          </ErrorBoundary>
        </div>
      </div>
      <div className={css.advancedSearchLinkContainer}>
        <div
          onClick={handleClearFiltersClick}
          className={css.advancedSearchLinks}
        >
          Clear Filters
          <span className={css.startOver}> (start over)</span>
        </div>
        <div
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          className={css.advancedSearchLinks}
        >
          {showAdvancedSearch ? (
            <span>&lsaquo; Basic Search</span>
          ) : (
            <span>Advanced Search &rsaquo;</span>
          )}
        </div>
      </div>
    </div>
  );
}

export default Filters;
